// Mensagem de retorno da análise de cancelamento (#5831, projeto 11).
//
// Monta o texto que o atendente revisa antes de responder o cliente. Base: o
// modelo que o Vinicius Alves passou, registrado na tarefa 07. O texto final
// ainda depende da demanda 01 — por isso MODELO_PROVISORIO abaixo.
//
// ⚠️ Os valores vêm SEMPRE do snapshot congelado na validação (demanda 05), não
// de um cálculo novo: entre validar e responder, saldo e faturas mudam, e o que
// o cliente recebe tem que ser o que o atendente conferiu.
//
// Regra do aceite: trecho sem valor SOME do texto. Nunca "R$ 0,00", nunca
// "multa de R$ null" — é mensagem que o cliente guarda e cobra depois.
//
// Sem I/O e sem Date.now().

import type { AnaliseFinanceira } from './analiseFinanceiraCancelamento.ts';

/** Enquanto a demanda 01 não aprova o texto oficial, a prévia avisa que é provisório. */
export const MODELO_PROVISORIO = true;

export interface EntradaMensagemRetorno {
  clienteNome: string | null | undefined;
  protocolo: string | null | undefined;
  dataSolicitacao: string | null | undefined;
  dataRetornoPrevista: string | null | undefined;
  /** null quando a solicitação foi validada antes da demanda 05 existir. */
  analise: AnaliseFinanceira | null;
}

export interface MensagemRetorno {
  texto: string;
  /** O que o atendente precisa saber antes de enviar (não vai para o cliente). */
  avisos: string[];
}

const brl = (v: number) => v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const kwh = (v: number) => `${Math.round(v).toLocaleString('pt-BR')} kWh`;

const dataBr = (v: string | null | undefined): string | null => {
  const t = String(v ?? '').trim().slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t)) return null;
  const [ano, mes, dia] = t.split('-');
  return `${dia}/${mes}/${ano}`;
};

const primeiroNome = (nome: string | null | undefined): string => {
  const limpo = String(nome ?? '').trim().replace(/\s+/g, ' ');
  if (!limpo) return '';
  const p = limpo.split(' ')[0];
  // Nome todo em caixa alta é o padrão do cadastro vindo da distribuidora.
  return p === p.toUpperCase() ? p.charAt(0) + p.slice(1).toLowerCase() : p;
};

/**
 * Monta a mensagem em blocos. Cada bloco só entra se tiver valor de verdade —
 * é isso que impede o "R$ 0,00" e o "conforme a cláusula undefined".
 */
export function montarMensagemRetorno(e: EntradaMensagemRetorno): MensagemRetorno {
  const avisos: string[] = [];
  const nome = primeiroNome(e.clienteNome);
  const protocolo = String(e.protocolo ?? '').trim();
  const dataPedido = dataBr(e.dataSolicitacao);
  const dataRetorno = dataBr(e.dataRetornoPrevista);

  if (!protocolo) avisos.push('Esta solicitação não tem protocolo — é anterior ao protocolo automático.');
  if (!e.analise) avisos.push('A validação desta solicitação não tem a análise financeira. Revalide para incluir saldo, faturas e multa na mensagem.');

  const partes: string[] = [];

  partes.push([
    nome ? `Olá, ${nome}!` : 'Olá!',
    'Estamos com o seu pedido de cancelamento',
    dataPedido ? `solicitado em ${dataPedido}` : null,
    protocolo ? `sob o protocolo ${protocolo}` : null,
  ].filter(Boolean).join(' ').replace(' Estamos', '\n\nEstamos') + '.');

  const a = e.analise;
  const pendencias: string[] = [];

  if (a?.saldo.valor?.temSaldo) {
    const meses = a.saldo.valor.mesesDeConsumo;
    pendencias.push(
      `saldo de energia de ${kwh(a.saldo.valor.saldoKwh)} na sua unidade`
      + (meses !== null && meses > 0 ? `, que cobre cerca de ${meses} mês(es) de consumo` : ''),
    );
  }
  if (a && a.faturasEmAberto.qtd > 0) {
    // Fatura em aberto pode existir com valor zerado (reemissão sem cobrança).
    // Nesse caso cita a quantidade e omite o valor — "R$ 0,00" numa cobrança é
    // o tipo de frase que o cliente usa contra a Vigor depois.
    pendencias.push(a.faturasEmAberto.valor > 0
      ? `${a.faturasEmAberto.qtd} fatura(s) em aberto, no total de ${brl(a.faturasEmAberto.valor)}`
      : `${a.faturasEmAberto.qtd} fatura(s) em aberto`);
  }
  if (a?.multa.valor) {
    // Cláusula 13.4-III: a cobrança vai com demonstrativo básico de cálculo.
    pendencias.push(
      `multa contratual de ${brl(a.multa.valor.valor)} (${Math.round(a.multa.valor.percentual * 100)}% `
      + `da contribuição média de ${brl(a.multa.valor.baseMensal)}, referente aos ${a.multa.valor.mesesVincendos} `
      + `mês(es) que faltam para completar a fidelidade)`,
    );
  } else if (a?.fidelidade.valor?.dentroDaFidelidade) {
    // Dentro da fidelidade, mas sem conseguir calcular: o atendente precisa
    // saber que o assunto existe e resolver por fora antes de responder.
    avisos.push(`O contrato ainda está na fidelidade (faltam ${a.fidelidade.valor.mesesRestantes} mês(es)),`
      + ` mas ${a.multa?.motivo ?? 'a multa não foi calculada'}. A mensagem sai sem citar multa.`);
  } else if (a?.carencia?.valor?.dentroDaCarencia) {
    avisos.push(`O cliente está na carência até ${a.carencia.valor.fimDaCarencia.split('-').reverse().join('/')}`
      + ' — sai sem multa, mas o saldo continua devido (cláusula 13.7).');
  }

  if (pendencias.length > 0) {
    partes.push(`Na análise do seu pedido, verificamos ${listar(pendencias)}.`);
    partes.push('Para seguirmos com o distrato, esses pontos precisam ser regularizados.');
  } else if (a) {
    partes.push('Na análise do seu pedido, não encontramos faturas em aberto nem saldo de energia a compensar.');
  }

  if (a) {
    // Quadro Resumo do Termo: a saída tem aviso prévio mínimo, e o cliente
    // precisa saber que não é desligamento imediato.
    partes.push(`Pelo contrato, a saída da associação tem aviso prévio de ${a.avisoPrevioDias ?? 90} dias,`
      + ' contado a partir do seu pedido.');
  }
  if (dataRetorno) partes.push(`O retorno com a conclusão está previsto para até ${dataRetorno}.`);
  partes.push('Qualquer dúvida, é só responder esta mensagem.');

  return { texto: partes.join('\n\n'), avisos };
}

/** "a, b e c" — sem vírgula antes do "e", como se escreve em português. */
export function listar(itens: string[]): string {
  if (itens.length === 0) return '';
  if (itens.length === 1) return itens[0];
  return `${itens.slice(0, -1).join(', ')} e ${itens[itens.length - 1]}`;
}

/** Limite prático de um texto que o atendente cola no WhatsApp (mesmo de mensagemCobranca). */
export const LIMITE_MENSAGEM_WHATSAPP = 1800;

// ===========================================================================
// Aviso de protocolo (#5830)
// ===========================================================================

export interface EntradaAvisoProtocolo {
  clienteNome: string | null | undefined;
  protocolo: string | null | undefined;
  dataSolicitacao: string | null | undefined;
  dataRetornoPrevista: string | null | undefined;
  ucNumero: string | null | undefined;
}

/**
 * Confirmação de que o pedido chegou. Só isso: nenhum valor, nenhuma promessa
 * sobre o resultado da análise — quem informa multa, saldo e faturas é a
 * mensagem de retorno, depois de um atendente conferir.
 *
 * Mesma regra do resto do projeto: trecho sem dado some do texto.
 */
export function textoAvisoProtocolo(e: EntradaAvisoProtocolo): string {
  const nome = primeiroNome(e.clienteNome);
  const protocolo = String(e.protocolo ?? '').trim();
  const dataPedido = dataBr(e.dataSolicitacao);
  const dataRetorno = dataBr(e.dataRetornoPrevista);
  const uc = String(e.ucNumero ?? '').trim();

  const partes: string[] = [];
  partes.push(nome ? `Olá, ${nome}!` : 'Olá!');
  partes.push([
    'Recebemos o seu pedido de cancelamento',
    uc ? `da unidade ${uc}` : null,
    dataPedido ? `em ${dataPedido}` : null,
  ].filter(Boolean).join(' ') + '.');
  if (protocolo) partes.push(`O número de protocolo é ${protocolo}. Guarde para acompanhar o pedido.`);
  partes.push(dataRetorno
    ? `Nossa equipe vai analisar e responder até ${dataRetorno}.`
    : 'Nossa equipe vai analisar e responder em breve.');
  partes.push('Qualquer dúvida, é só responder esta mensagem.');

  return partes.join('\n\n');
}
